import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { useUnsavedChangesStore } from '@/store/unsavedChangesStore';

const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
};

const DEFAULT_DIALOG_TITLE = 'Are you sure you want to leave this page?';
const DEFAULT_DIALOG_TEXT = 'You have unsaved changes that will be lost.';

export const UnsavedChangesBlocker = () => {
    const { hasUnsavedChanges, isNavigationBlocked, confirmNavigation, cancelNavigation, currentDialogCopy } =
        useUnsavedChangesStore(
            useShallow((s) => ({
                hasUnsavedChanges: s.hasUnsavedChanges,
                isNavigationBlocked: s.isNavigationBlocked,
                confirmNavigation: s.confirmNavigation,
                cancelNavigation: s.cancelNavigation,
                currentDialogCopy: s.currentDialogCopy,
            }))
        );
    const blocker = useBlocker(hasUnsavedChanges);
    const isOpen = isNavigationBlocked || blocker.state === 'blocked';
    const dialogCopy = currentDialogCopy();

    const handleCancel = () => {
        blocker.reset?.();
        cancelNavigation();
    };

    const handleLeave = () => {
        blocker.proceed?.();
        confirmNavigation();
    };

    useEffect(() => {
        if (!hasUnsavedChanges) {
            return;
        }

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleCancel}
            aria-labelledby="unsaved-changes-dialog-title"
            aria-describedby="unsaved-changes-dialog-description"
        >
            <DialogTitle id="unsaved-changes-dialog-title">{dialogCopy.title ?? DEFAULT_DIALOG_TITLE}</DialogTitle>
            <DialogContent>
                <DialogContentText id="unsaved-changes-dialog-description">
                    {dialogCopy.text ?? DEFAULT_DIALOG_TEXT}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleCancel} autoFocus>
                    Cancel
                </Button>
                <Button onClick={handleLeave} variant="contained">
                    Yes, leave page
                </Button>
            </DialogActions>
        </Dialog>
    );
};
